import React from 'react';
import { getContext, useCombinedRefs, getEasing, getDuration, isAnimationDisabled } from 'common';
import useClickOutside from 'common/useClickOutside';
import { ElevationProps, getElevationStyle } from 'components/Elevation';
import { usePopper } from 'react-popper';
import { useTransition, animated } from 'react-spring';
import { SPEED_FAST } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { TRANSITION_DECELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import Styled from './styles';

const DEFAULT_POPPER_CONFIG = arrowElement => ({
  strategy: 'absolute' as const,
  placement: 'auto' as const,
  modifiers: [
    {
      name: 'arrow',
      options: {
        element: arrowElement,
        enabled: true
      }
    },
    { name: 'flip', options: { enabled: true } },
    { name: 'offset', options: { offset: [-18, 33] } },
    {
      name: 'preventOverflow',
      options: {
        enabled: true,
        rootBoundary: 'viewport',
        altAxis: true,
        padding: 5
      }
    }
  ]
});

// React-spring config for 'related' variant
const DEFAULT_RELATED_ANIMATION = disableAnimation => ({
  from: { opacity: 0, transform: 'scale(0)', willChange: 'transform, opacity' },
  enter: { opacity: 1, transform: 'scale(1)', willChange: 'transform, opacity' },
  leave: { opacity: 0, transform: 'scale(0)', willChange: 'transform, opacity' },
  config: {
    duration: getDuration(SPEED_FAST),
    easing: getEasing(TRANSITION_DECELERATE)
  },
  immediate: disableAnimation,
});

// React-spring config for 'global' variant
const DEFAULT_GLOBAL_ANIMATION = disableAnimation => ({
  from: {
    opacity: 0,
    transform: 'perspective(1px) scale(0.6)',
    willChange: 'transform'
  },
  // perspective, willChange properties used to enhance smoothness of animation in Webkit & Firefox
  enter: { opacity: 1, transform: 'perspective(1px) scale(1)', willChange: 'transform, opacity' },
  leave: { opacity: 0, transform: 'perspective(1px) scale(0.6)', willChange: 'transform, opacity' },
  config: {
    duration: getDuration(SPEED_FAST),
    easing: getEasing(TRANSITION_DECELERATE)
  },
  immediate: disableAnimation,
});

/* quick function to retrieve the element (from string or ref) */
const getTargetRef = (target) => {
  if (typeof target === 'string' && document.querySelector(target)) {
    return document.querySelector(target);
  } else if (React.isValidElement(target)) {
    return target;
  } else if (target.current) {
    return target.current;
  }
  return undefined;
};

export interface CoachmarkProps extends React.ComponentPropsWithRef<'div'> {
  /** Toggles weather the coachmark is shown or not. */
  show?: boolean;
  /** Switch between Coachmark variants. Default coachmarks tour, places the individual coachmarks next to their target elements, whereas `global` variant places the coachmarks in a more universal way, centering them on the screen. */
  variant?: 'related' | 'global';
  /** Coachmark content is placed here. See demo example and sub-components for suggested composition. */
  children: React.ReactNode;
  /** Target element. Pass in a ref or a selector string. */
  target?: string | Element | {
    current?: Element
  };
  /** Callback function to be called when user clicks outside of the coachmark.  */
  onClickOutside?: React.MouseEventHandler;
  /** Toggle balloon visuals. Balloon is beak/arrow like indicator that points to the target element. */
  balloon?: boolean;
  /** Pass in popper options to fine tune placement (or other modifiers) of the coachmark. See https://popper.js.org/docs/v2/ for more information on possible options. */
  positioningOptions?: Record<string, any>;
  /** Controls animation for coachmark. See react-spring transition config for more information (https://react-spring.io). */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** Forwards props to the container element (root element). */
  containerProps?: React.ComponentPropsWithRef<'div'>;
  /** The container's <Elevation /> configuration, see the `<Elevation/>` documentation for more. */
  elevationProps?: Partial<ElevationProps>;
  /** @ignore */
  style?: React.CSSProperties;
}

/** Single coachmark. Place in <Coachmarks /> component to create a tour.
 * All additional props will be forwarded to the coachmark element.
 * To pass props to the root element make use of the 'containerProps' prop.
 */
const Coachmark = React.forwardRef<HTMLElement, CoachmarkProps>((props: CoachmarkProps, ref) =>
  getContext(({ RTL, disableAnimation }) => {
    const {
      target,
      show,
      variant,
      children,
      balloon,
      onClickOutside,
      positioningOptions,
      animationProps,
      disableAnimation: propsdisableAnimation,
      containerProps,
      elevationProps,
      style,
      ...otherProps
    } = props;
    const targetRef = target && getTargetRef(target);
    const [arrowElement, setArrowElement] = React.useState(null);
    const tooltipRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const combinedRef = useCombinedRefs(ref, containerRef);
    const { styles, attributes } = usePopper(targetRef, combinedRef.current, {
      ...DEFAULT_POPPER_CONFIG(arrowElement),
      ...positioningOptions
    });
    const isGlobal = variant === 'global';
    const transitions = useTransition(show, null, animationProps || DEFAULT_RELATED_ANIMATION(isAnimationDisabled(propsdisableAnimation, disableAnimation)));
    const transitionsGlobal = useTransition(show, null, animationProps || DEFAULT_GLOBAL_ANIMATION(isAnimationDisabled(propsdisableAnimation, disableAnimation)));
    onClickOutside && useClickOutside(tooltipRef, onClickOutside);
    const resolvedTransition = isGlobal ? transitionsGlobal : transitions;
    const popperAttributes = !isGlobal && targetRef ? attributes.popper : {};
    const coachmarkStyles = !isGlobal && targetRef ? styles.popper : {};
    const elevationStyles = getElevationStyle(elevationProps);
    return (
      <Styled.CoachmarkContainer
        ref={combinedRef}
        $rtl={RTL}
        $global={variant === 'global'}
        {...popperAttributes}
        style={coachmarkStyles}
        {...containerProps}
      >
        {resolvedTransition.map(({ item, key, props: transitionStyles }) =>
          item && (
          <Styled.CoachmarkAnimation
            ref={tooltipRef}
            as={animated.div}
            key={key}
            {...popperAttributes}
            style={{ ...transitionStyles }}
          >
            <Styled.Coachmark
              role="alertdialog"
              aria-modal
              style={{ ...elevationStyles, ...style }}
              {...otherProps}
            >
              {children}
              {!isGlobal && balloon && (
              <Styled.CoachmarkBalloon
                ref={setArrowElement}
                $elevationStyles={elevationStyles}
                style={styles.arrow}
                {...attributes.popper}
              />
              )}
            </Styled.Coachmark>
          </Styled.CoachmarkAnimation>
          ))}
      </Styled.CoachmarkContainer>
    );
  }));

Coachmark.displayName = 'Coachmark';
Coachmark.defaultProps = {
  variant: 'related',
  balloon: true,
  show: true,
  positioningOptions: undefined,
  target: undefined,
  onClickOutside: undefined,
  animationProps: undefined,
  disableAnimation: undefined,
  containerProps: {},
  elevationProps: {
    elevationIndex: 24
  },
  style: {}
};

/* Exports */
export { Coachmark as default };
